/**
 * This function creates and returns a new instance of the ConditionalExecutor class, which facilitates
 * conditional branching and action execution based on specific conditions defined later.
 *
 * The `switcher` function is a versatile utility designed to simplify conditional branching and action execution in TypeScript. It is particularly useful when you have multiple conditions to evaluate against a value and need a clean and readable way to specify the corresponding actions for each condition.
 * #### When to Use `switcher`:
 * 1. **Multiple Conditional Branching:** `switcher` is an excellent choice when you have several conditions to check against a value and want to avoid nesting multiple `if` statements, resulting in more concise and maintainable code.
 * 2. **Type-Guarded Matching:** It's especially handy when you want to match a value based on TypeScript type guards, making it easy to perform type-specific actions when conditions are met.
 * 3. **Fallback Handling:** `switcher` allows you to define a fallback action (via the `none` method) for cases where none of the conditions match, providing a clean way to handle unexpected or unhandled scenarios.
 *
 * #### When Not to Use `switcher`:
 * 1. **Simple Single-Condition Checks:** For straightforward scenarios with only one or two conditions to evaluate, using plain `if` statements may be more straightforward and readable.
 * 2. **Performance-Critical Code:** In highly performance-critical sections of your code, `switcher` may introduce a small amount of overhead due to its method chaining and array-based condition checking. In such cases, optimizing for performance might lead to alternative approaches.
 *
 * In summary, `switcher` is a powerful tool for managing complex conditional logic with a focus on readability and maintainability.
 * It excels in scenarios where you need to handle multiple conditions gracefully and efficiently.
 * However, for simpler and performance-critical code, using traditional `if` statements might be more appropriate.
 *
 * @param value - The initial value to be used for matching (optional).
 * @returns A new ConditionalExecutor instance that allows you to define and execute conditions and actions.
 *
 * @example
 * // Create a ConditionalExecutor instance with an initial value of 5.
 * const myConditionExecutor = switcher(5);
 *
 * // Define conditions and actions for the ConditionalExecutor instance.
 * myConditionExecutor.when((val) => val === 0, (val) => console.log('Value is zero.'));
 * myConditionExecutor.when((val) => val === 5, (val) => console.log('Value is five.'));
 * myConditionExecutor.none((val) => console.log('No condition matched.'));
 *
 * // Match the value and execute the appropriate action.
 * myConditionExecutor.match(); // Output: 'Value is five.'
 * myConditionExecutor.match(0); // Output: 'Value is zero.'
 * myConditionExecutor.match(3); // Output: 'No condition matched.'
 *
 * @author Aakash More
 */
export function switcher<T, R = void>(value?: T) {
  return new ConditionalExecutor<T, R>(value);
}

interface DifferMatch<T> {
  condition: ((val: T) => boolean) | T | T[];
  then: (val: T) => void;
}

/**
 * The ConditionalExecutor class allows you to define multiple conditions and their corresponding actions
 * based on a value, and then later execute the appropriate action based on the matching condition.
 */
class ConditionalExecutor<T = unknown, R = void> {
  protected matchers = [] as DifferMatch<T>[];
  protected onNoneMatch = null as null | ((val: T) => void);
  constructor(protected val?: T) {}

  /**
   * Define a condition and its corresponding action for matching a value using a type guard.
   * This overload is useful for distinguishing between different event types.
   *
   * @param condition - The type guard condition to be checked against the value.
   * @param then - The action to execute if the condition is met with the specified type.
   * @returns The current ConditionalExecutor instance for method chaining.
   *
   * @example
   * const executor = switcher<Event, void>(new MouseEvent('click'));
   * // Define a type guard condition to differentiate between mouse and keyboard events.
   * executor.when((val): val is MouseEvent => val instanceof MouseEvent, (mouseEvent) => {
   *   // The type of mouseEvent is automatically inferred as MouseEvent here.
   *   console.log('Mouse event detected:');
   *   console.log('ClientX:', mouseEvent.clientX);
   *   console.log('ClientY:', mouseEvent.clientY);
   * });
   *
   * executor.when((val): val is KeyboardEvent => val instanceof KeyboardEvent, (keyboardEvent) => {
   *   // The type of keyboardEvent is automatically inferred as KeyboardEvent here.
   *   console.log('Keyboard event detected:');
   *   console.log('Key:', keyboardEvent.key);
   * });
   *
   * // Match and execute the action with a MouseEvent.
   * const mouseEvent = new MouseEvent('click', { clientX: 100, clientY: 200 });
   * executor.match(mouseEvent);
   * // Output:
   * // Mouse event detected:
   * // ClientX: 100
   * // ClientY: 200
   *
   * // Match and execute the action with a KeyboardEvent.
   * const keyboardEvent = new KeyboardEvent('keydown', { key: 'Enter' });
   * executor.match(keyboardEvent);
   * // Output:
   * // Keyboard event detected:
   * // Key: Enter
   */
  when<K extends T>(condition: (val: T) => val is K, then: (val: K) => void): this;
  /**
   * Define a condition and its corresponding action for matching a value based on a custom condition.
   *
   * @param condition - The condition function to be checked against the value.
   * @param then - The action to execute if the condition is met.
   * @returns The current ConditionalExecutor instance for method chaining.
   *
   * @example
   * const executor = new ConditionalExecutor<number, void>(5);
   *
   * // Define a custom condition for matching numbers greater than 10.
   * executor.when((val) => val > 10, (val) => console.log(`${val} is greater than 10.`));
   *
   * // Match and execute the action.
   * executor.match(); // No output because the initial value is not greater than 10.
   * executor.match(15); // Output: '15 is greater than 10.'
   */
  when(condition: (val: T) => boolean, then: (val: T) => void): this;
  /**
   * Define a condition and its corresponding action for matching a value based on a list of possible values.
   *
   * @param condition - An array of values to compare against.
   * @param then - The action to execute if the value matches any in the list.
   * @returns The current ConditionalExecutor instance for method chaining.
   *
   * @example
   * const executor = switcher<string, void>('apple');
   *
   * // Define a condition for matching specific fruit names.
   * executor
   *  .when(['apple', 'banana', 'cherry'], (val) => console.log(`It's a ${val}.`))
   *  .when('pineapple', val => console.log("It's a pineapple"))
   *
   * // Match and execute the action.
   * executor.match(); // No output because the initial value is not in the list.
   * executor.match('banana'); // Output: 'It's a banana.'
   * executor.match('pineapple'); // Output: 'It's a pineapple.'
   */
  when<Y extends T>(condition: Y | Y[], then: (val: Y) => void): this;
  when<K extends T>(condition: (val: T) => boolean | ((val: T) => val is K) | T | T[], then: (val: K) => void) {
    this.matchers.push({ condition, then } as DifferMatch<T>);
    return this;
  }

  /**
   * Define an action to be executed when none of the conditions in the `when` method are met.
   * Use this function to provide a fallback action if none of the defined conditions match.
   *
   * @param then - The action to execute if none of the conditions are met.
   * @returns The current ConditionalExecutor instance for method chaining.
   *
   * @example
   * const executor = switcher(); // Creating a ConditionalExecutor without an initial value.
   * // Define conditions for matching even and odd numbers.
   * executor.when((val) => val % 2 === 0, (val) => console.log(`${val} is even.`));
   * executor.when((val) => val % 2 !== 0, (val) => console.log(`${val} is odd.`));
   *
   * // Define a fallback action for values that don't match any condition.
   * executor.none((val) => console.log(`${val} doesn't match any condition.`));
   *
   * // Match and execute actions for different values.
   * executor.match(4); // Output: '4 is even.'
   * executor.match(7); // Output: '7 is odd.'
   * executor.match(2.5); // Output: '2.5 doesn't match any condition.'
   */
  none(then: (val: T) => void): this {
    this.onNoneMatch = then;
    return this;
  }

  /**
   * Match the provided value or the initial value against the defined conditions and execute the appropriate action.
   *
   * @typeparam P - The type of the result returned when matching.
   * @param val - The value to be matched (optional). If not provided, the initial value set in the `switcher` function (if used) will be used for matching.
   * @returns The result of the matched action or the result of the "none" action if no condition is met.
   *
   * @example
   * const executor = switcher(5); // Creating a ConditionalExecutor with an initial value of 5.
   * // Define conditions and actions for the ConditionalExecutor.
   * executor.when((val) => val === 0, (val) => console.log('Value is zero.'));
   * executor.when((val) => val === 5, (val) => console.log('Value is five.'));
   * executor.none((val) => console.log('No condition matched.'));
   *
   * // Match the value and execute the appropriate action.
   * executor.match(); // Output: 'Value is five.' (Using the initial value)
   * executor.match(0); // Output: 'Value is zero.'
   * executor.match(3); // Output: 'No condition matched.'
   *
   * // Creating a new ConditionalExecutor without an initial value.
   * const executor2 = switcher();
   * executor2.when((val) => val === 'apple', (val) => console.log('It\'s an apple.'));
   * executor2.none((val) => console.log('No condition matched.'));
   *
   * // Match with no value provided, using the initial value (undefined).
   * executor2.match(); // Output: 'No condition matched.'
   * executor2.match('apple'); // Output: It's an apple.
   */
  match<P = R>(val?: T): P {
    const value = (val ?? this.val) as T;
    const found = this.matchers.find(({ condition }) =>
      Array.isArray(condition)
        ? condition.includes(value)
        : typeof condition === 'function'
          ? (condition as (val: T) => boolean)(value)
          : value === condition
    );
    if (found) return found.then(value) as unknown as P;
    return this.onNoneMatch?.(value) as unknown as P;
  }

  /**
   * Test if the value exists in the switcher
   */
  test(val?: T) {
    const value = (val ?? this.val) as T;
    const found = this.matchers.find(({ condition }) =>
      Array.isArray(condition)
        ? condition.includes(value)
        : typeof condition === 'function'
          ? (condition as (val: T) => boolean)(value)
          : value === condition
    );
    return Boolean(found);
  }

  clone() {
    const instance = switcher<T, R>();
    instance.matchers = [...this.matchers];
    instance.onNoneMatch = this.onNoneMatch;
    return instance;
  }
}

export type Switcher<T = unknown, R = void> = ConditionalExecutor<T, R>;
